import React, {
  ComponentPropsWithoutRef,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react"

interface ScrollSpyContextType {
  current: string | null;
  setCurrent: (name: string) => void;
  addAnchor: (anchor: HTMLAnchorElement) => void;
  removeAnchor: (anchor: HTMLAnchorElement) => void;
}

const ScrollSpyContext = createContext<ScrollSpyContextType>(
  {} as unknown as ScrollSpyContextType
)

export const useCurrentAnchor = () => {
  const { current } = useContext(ScrollSpyContext)
  return current
}

interface ScrollSpyProps {
  location: { hash: string };
  offset?: number;
  children: ReactNode;
}

export const ScrollSpy: FC<ScrollSpyProps> = ({
  location,
  offset = 10,
  children,
}) => {
  const anchors = useRef<HTMLAnchorElement[]>([])
  const [current, setCurrent] = useState<string | null>(null)

  // マウント後、URLに対応するAnchorまでスクロール
  useEffect(() => {
    if (location.hash) {
      const name = location.hash.slice(1)
      const anchor = anchors.current.find((anchor) => anchor.id === name)
      if (anchor) {
        anchor.scrollIntoView()
        setCurrent(name)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight =
        document.documentElement.scrollHeight || document.body.scrollHeight
      const windowHeight = window.innerHeight
      const scrollTop =
        document.documentElement.scrollTop || document.body.scrollTop

      if (anchors.current.length === 0) {
        return
      }
      let currentAnchor = anchors.current[0]
      if (!currentAnchor) {
        return
      }
      let currentAnchorTop = currentAnchor.getBoundingClientRect().top
      for (const anchor of anchors.current) {
        const top = anchor.getBoundingClientRect().top - offset
        if (top <= 0 && (top > currentAnchorTop || currentAnchorTop > 0)) {
          currentAnchorTop = top
          currentAnchor = anchor
        }
      }
      // スクロール位置が最下部の場合、最後のAnchorを選択
      if (windowHeight + scrollTop >= scrollHeight - offset) {
        currentAnchor = anchors.current[anchors.current.length - 1] as HTMLAnchorElement
      }

      const { id: name } = currentAnchor
      setCurrent(name)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [offset])

  const addAnchor = useCallback((anchor: HTMLAnchorElement) => {
    if (anchors.current.length === 0) {
      setCurrent(anchor.id)
    }
    anchors.current = [...anchors.current, anchor]
  }, [])

  const removeAnchor = useCallback((anchor: HTMLAnchorElement) => {
    anchors.current = anchors.current.filter((a) => a !== anchor)
  }, [])

  return (
    <ScrollSpyContext.Provider value={{ current, setCurrent, addAnchor, removeAnchor }}>
      {children}
    </ScrollSpyContext.Provider>
  )
}

interface AnchorTargetProps extends ComponentPropsWithoutRef<"a"> {
  name: string;
}

export const AnchorTarget: FC<AnchorTargetProps> = ({ name, ...rest }) => {
  const { addAnchor, removeAnchor } = useContext(ScrollSpyContext)
  if (!addAnchor || !removeAnchor) {
    throw "AnchorTarget must be used inside ScrollSpy"
  }

  const ref = useMemo(() => {
    let prev: HTMLAnchorElement | null = null
    return (element: HTMLAnchorElement | null) => {
      if (element) {
        prev = element
        addAnchor(element)
      } else if (prev) {
        removeAnchor(prev)
      }
    }
  }, [addAnchor, removeAnchor])

  return <a {...rest} id={name} ref={ref} />
}

interface AnchorLinkProps extends ComponentPropsWithoutRef<"a"> {
  name: string;
}

export const AnchorLink: FC<AnchorLinkProps> = ({
  children,
  name,
  ...rest
}) => {
  const { setCurrent } = useContext(ScrollSpyContext)
  if (!setCurrent) {
    throw "AnchorLink must be used inside ScrollSpy"
  }

  const handleClick = () => {
    setTimeout(() => setCurrent(name), 0)
  }

  return (
    <a {...rest} href={`#${name}`} onClick={handleClick}>
      {children}
    </a>
  )
}
